import { Injectable } from '@angular/core';
import { getAuth } from 'firebase/auth';
import { from, switchMap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { AppSettingsService } from './app-settings.service';

export interface StripeCheckoutBillingDetails {
  billingForCompany: boolean;
  name?: string | null;
  email?: string | null;
  companyName?: string | null;
  taxId?: string | null;
  companyRegistrationNumber?: string | null;
  address?: {
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
    country?: string | null;
  } | null;
}

interface CreateStripeCheckoutSessionPayload {
  priceId: string;
  accountId: string;
  userId: string;
  userEmail: string;
  customerName?: string | null;
  billingDetails?: StripeCheckoutBillingDetails | null;
  planLevel: string;
  planName: string;
  subscriptionId: string | null;
  paymentId: string;
  cancelStripeSubscriptionOnSuccess?: string | null;
  previousAppSubscriptionId?: string | null;
  promotionCode?: string | null;
}

interface CreateStripeCheckoutSessionResponse {
  url: string;
  sessionId: string;
}

export interface StripeBillingPreviewPayload {
  priceId: string;
  accountId: string;
  userId: string;
  subscriptionId: string | null;
  previousSubscriptionId?: string | null;
  promotionCode?: string | null;
}

export interface StripeBillingPreviewResponse {
  amount_due_now: number;
  subtotal: number;
  proration_amount: number;
  discount_amount: number;
  currency: string;
  next_renewal_amount: number | null;
  effective_date: string | null;
  estimate_label: string;
  currency_mismatch?: boolean;
  current_subscription_currency?: string;
}


export interface StripeCheckoutStatusSyncPayload {
  accountId: string;
  userId: string;
  paymentId: string;
  subscriptionId?: string | null;
}

export interface StripeCheckoutStatusSyncResponse {
  stripeSubscriptionId: string | null;
  stripeSubscriptionStatus: string | null;
  paymentStatus: 'active' | 'past_due' | 'canceled' | 'pending';
  error?: string;
}

export interface StripeSubscriptionUpgradePayload {
  accountId: string;
  userId: string;
  subscriptionId: string;
  previousSubscriptionId?: string | null;
  priceId: string;
  planLevel: string;
  planName: string;
  paymentId: string;
  prorationBehavior?: 'always_invoice' | 'create_prorations' | 'none';
  promotionCode?: string | null;
}

export interface StripeSubscriptionUpgradeResponse {
  status: 'updated' | 'unchanged';
  stripeSubscriptionId: string;
  stripeSubscriptionStatus: string | null;
  paymentStatus: 'active' | 'past_due' | 'canceled' | 'pending';
  currentPeriodStart: number | null;
  currentPeriodEnd: number | null;
  latestInvoiceId: string | null;
  amountDueNow: number | null;
  prorationAmount: number | null;
  currency: string | null;
}

interface CreateStripeCheckoutSessionErrorResponse {
  error?: string;
  details?: string;
  code?: string;
  currentCurrency?: string;
  newCurrency?: string;
  stripeSubscriptionId?: string;
}

export interface StripeCheckoutHttpError extends Error {
  status?: number;
  code?: string;
  currentCurrency?: string;
  newCurrency?: string;
  stripeSubscriptionId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class StripeCheckoutService {
  constructor(private readonly appSettingsService: AppSettingsService) {}

  createCheckoutSession(payload: CreateStripeCheckoutSessionPayload) {
    return from(this.getAuthToken()).pipe(
      switchMap(token => {
        if (!token) {
          return throwError(() => new Error('Authentication token unavailable.'));
        }

        return from(fetch(this.resolveStripeCheckoutSessionUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        }));
      }),
      switchMap(async response => {
        const payload = await this.parseResponsePayload(response) as Partial<CreateStripeCheckoutSessionResponse> & CreateStripeCheckoutSessionErrorResponse;

        if (!response.ok) {
          const baseMessage = payload?.error || 'Unable to create Stripe checkout session.';
          const details = payload?.details?.trim();
          const errorMessage = !environment.production && details
            ? `${baseMessage} ${details}`
            : baseMessage;
          throw this.buildHttpError(response.status, errorMessage);
        }

        if (!payload.url || !payload.sessionId) {
          throw new Error('Stripe checkout session response is incomplete.');
        }

        return payload as CreateStripeCheckoutSessionResponse;
      })
    );
  }

  previewBilling(payload: StripeBillingPreviewPayload) {
    return from(this.getAuthToken()).pipe(
      switchMap(token => {
        if (!token) {
          return throwError(() => new Error('Authentication token unavailable.'));
        }

        return from(fetch(this.resolveStripeBillingPreviewUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        }));
      }),
      switchMap(async response => {
        const payload = await this.parseResponsePayload(response) as Partial<StripeBillingPreviewResponse> & CreateStripeCheckoutSessionErrorResponse;

        if (!response.ok) {
          const baseMessage = payload?.error || 'Unable to preview Stripe billing.';
          const details = payload?.details?.trim();
          const errorMessage = !environment.production && details
            ? `${baseMessage} ${details}`
            : baseMessage;
          throw this.buildHttpError(response.status, errorMessage);
        }

        if (typeof payload.amount_due_now !== 'number' || !payload.currency) {
          throw new Error('Stripe billing preview response is incomplete.');
        }

        return payload as StripeBillingPreviewResponse;
      })
    );
  }


  upgradeSubscription(payload: StripeSubscriptionUpgradePayload) {
    return from(this.getAuthToken()).pipe(
      switchMap(token => {
        if (!token) {
          return throwError(() => new Error('Authentication token unavailable.'));
        }

        return from(fetch(this.resolveStripeSubscriptionUpgradeUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        }));
      }),
      switchMap(async response => {
        const responsePayload = await this.parseResponsePayload(response) as Partial<StripeSubscriptionUpgradeResponse> & CreateStripeCheckoutSessionErrorResponse;

        if (!response.ok) {
          const baseMessage = responsePayload?.error || 'Unable to upgrade Stripe subscription.';
          const details = responsePayload?.details?.trim();
          const errorMessage = !environment.production && details
            ? `${baseMessage} ${details}`
            : baseMessage;
          const httpError = this.buildHttpError(response.status, errorMessage);
          if (responsePayload?.code) httpError.code = responsePayload.code;
          if (responsePayload?.currentCurrency) httpError.currentCurrency = responsePayload.currentCurrency;
          if (responsePayload?.newCurrency) httpError.newCurrency = responsePayload.newCurrency;
          if (responsePayload?.stripeSubscriptionId) httpError.stripeSubscriptionId = responsePayload.stripeSubscriptionId;
          throw httpError;
        }

        if (!responsePayload.stripeSubscriptionId || !responsePayload.paymentStatus) {
          throw new Error('Stripe subscription upgrade response is incomplete.');
        }

        return responsePayload as StripeSubscriptionUpgradeResponse;
      })
    );
  }

  syncCheckoutStatus(payload: StripeCheckoutStatusSyncPayload) {
    return from(this.getAuthToken()).pipe(
      switchMap(token => {
        if (!token) {
          return throwError(() => new Error('Authentication token unavailable.'));
        }

        return from(fetch(this.resolveStripeCheckoutStatusSyncUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        }));
      }),
      switchMap(async response => {
        const payload = await this.parseResponsePayload(response) as Partial<StripeCheckoutStatusSyncResponse> & CreateStripeCheckoutSessionErrorResponse;

        if (!response.ok) {
          const baseMessage = payload?.error || 'Unable to sync Stripe checkout status.';
          const details = payload?.details?.trim();
          const errorMessage = !environment.production && details
            ? `${baseMessage} ${details}`
            : baseMessage;
          throw this.buildHttpError(response.status, errorMessage);
        }

        if (!payload.paymentStatus) {
          throw new Error('Stripe checkout sync response is incomplete.');
        }

        return payload as StripeCheckoutStatusSyncResponse;
      })
    );
  }

  private async getAuthToken(): Promise<string | null> {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      return null;
    }

    return user.getIdToken();
  }

  private resolveStripeCheckoutSessionUrl(): string {
    const liveModeEnabled = this.appSettingsService.stripeLivePaymentsEnabled();

    if (liveModeEnabled && environment.stripeCheckoutSessionLiveUrl.trim()) {
      return environment.stripeCheckoutSessionLiveUrl;
    }

    return environment.stripeCheckoutSessionSandboxUrl || environment.stripeCheckoutSessionUrl;
  }


  private resolveStripeCheckoutStatusSyncUrl(): string {
    const liveModeEnabled = this.appSettingsService.stripeLivePaymentsEnabled();

    if (liveModeEnabled && environment.stripeCheckoutStatusSyncLiveUrl.trim()) {
      return environment.stripeCheckoutStatusSyncLiveUrl;
    }

    return environment.stripeCheckoutStatusSyncSandboxUrl || environment.stripeCheckoutStatusSyncUrl;
  }

  private resolveStripeSubscriptionUpgradeUrl(): string {
    const liveModeEnabled = this.appSettingsService.stripeLivePaymentsEnabled();

    if (liveModeEnabled && environment.stripeSubscriptionUpgradeLiveUrl.trim()) {
      return environment.stripeSubscriptionUpgradeLiveUrl;
    }

    return environment.stripeSubscriptionUpgradeSandboxUrl || environment.stripeSubscriptionUpgradeUrl;
  }

  private resolveStripeBillingPreviewUrl(): string {
    const liveModeEnabled = this.appSettingsService.stripeLivePaymentsEnabled();

    if (liveModeEnabled && environment.stripeBillingPreviewLiveUrl.trim()) {
      return environment.stripeBillingPreviewLiveUrl;
    }

    return environment.stripeBillingPreviewSandboxUrl || environment.stripeBillingPreviewUrl;
  }

  private buildHttpError(status: number, message: string): StripeCheckoutHttpError {
    const error = new Error(message) as StripeCheckoutHttpError;
    error.status = status;
    return error;
  }

  private async parseResponsePayload(response: Response): Promise<Record<string, unknown>> {
    const raw = await response.text();
    if (!raw) {
      return {};
    }

    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
}
